import { CircleChevronRight } from "@/icons/CircleChevronRight.tsx";
import { CircleUser } from "@/icons/CircleUser.tsx";
import { Coin } from "@/icons/Coin.tsx";
import { FolderImage } from "@/icons/FolderImage.tsx";
import { FolderUser } from "@/icons/FolderUser.tsx";
import { Gem } from "@/icons/Gem.tsx";
import { getStats } from "@/services/stats.service.ts";
import useUserStore from "@/store/user.store.ts";
import {
  Avatar,
  Badge,
  Box,
  Card,
  getGradient,
  Paper,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link } from "react-router";

export function UserCard() {
  const { user, updateCredits } = useUserStore();
  const theme = useMantineTheme();

  const { data } = useQuery({
    queryKey: ["stats"],
    queryFn: () => getStats(),
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const credits = +data.credits;
    updateCredits(credits);
  }, [data]);

  if (!user) {
    return null;
  }

  return (
    <Card withBorder padding="xl" radius="md">
      <Card.Section className="relative">
        <Box
          style={{
            background: getGradient(
              { deg: 45, from: "grape.8", to: "violet.9" },
              theme,
            ),
          }}
          h="100%"
        >
          <div className="relative flex px-3 pt-6 pb-14">
            {user.premium === true && (
              <div className="absolute right-2 top-2">
                <Tooltip
                  label="Your photos remain protected and won’t be deleted, even without an active subscription"
                  withArrow
                >
                  <Badge color="lime" leftSection={<Gem size={10} />}>
                    Premium
                  </Badge>
                </Tooltip>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Avatar
                src={user?.avatar}
                size={80}
                radius="md"
                mx="auto"
                name={user?.name}
              >
                <CircleUser style={{ color: "white" }} size={64} />
              </Avatar>

              <div className="flex flex-col">
                <Text fz="lg" fw={600} c="white">
                  Welcome {user?.name}
                </Text>
                <Text fz="sm" c="white">
                  {user?.email}
                </Text>
              </div>
            </div>
          </div>
        </Box>

        <div className="grid grid-cols-2 p-2 -mt-10 gap-2">
          <Paper component={Link} to="/characters" withBorder shadow="sm">
            <div className="flex justify-between items-center gap-2 p-4 relative overflow-hidden">
              <div className="flex flex-col">
                <Text size="lg">
                  {data?.characters
                    ? new Intl.NumberFormat().format(data.characters)
                    : 0}
                </Text>
                <Text size="sm" c="dimmed">
                  Characters
                </Text>
              </div>

              <FolderUser className="opacity-40 absolute right-4" size={40} />
            </div>
          </Paper>
          <Paper component={Link} to="/playground" withBorder shadow="sm">
            <div className="flex justify-between items-center gap-2 p-4 relative overflow-hidden">
              <div className="flex flex-col">
                <Text size="lg">
                  {data?.photos
                    ? new Intl.NumberFormat().format(data.photos)
                    : 0}
                </Text>
                <Text size="sm" c="dimmed">
                  Photos
                </Text>
              </div>

              <FolderImage className="opacity-40 absolute right-4" size={40} />
            </div>
          </Paper>
          <Paper
            className="col-span-2"
            component={Link}
            to="/billing"
            withBorder
            shadow="sm"
          >
            <div className="flex justify-between items-center gap-2 p-4 relative overflow-hidden">
              <div className="flex flex-col">
                <Text size="lg">
                  {new Intl.NumberFormat().format(user.credits)}
                </Text>
                <Text size="sm" c="dimmed">
                  Credits
                </Text>
              </div>

              <Coin className="opacity-40 absolute right-4" size={40} />
            </div>
          </Paper>

          <Card
            component={Link}
            to="/characters"
            className="col-span-2"
            withBorder
            p="md"
            radius="md"
            w="100%"
          >
            <div className="flex gap-4 justify-between items-center flex-row flex-nowrap">
              <div className="flex flex-col">
                <Text fz="lg">Characters</Text>
                <Text c="gray" fz="sm">
                  Train AI characters
                </Text>
              </div>

              <CircleChevronRight size={24} />
            </div>
          </Card>

          <Card
            component={Link}
            to="/playground"
            className="col-span-2"
            withBorder
            p="md"
            radius="md"
            w="100%"
            style={{
              background: getGradient(
                { deg: 45, from: "grape", to: "violet.7" },
                theme,
              ),
            }}
          >
            <div className="flex gap-4 justify-between items-center flex-row flex-nowrap">
              <div className="flex flex-col">
                <Text c="white" fz="lg">
                  Playground
                </Text>
                <Text c="white" fz="sm">
                  Generate AI photos
                </Text>
              </div>

              <CircleChevronRight style={{ color: "white" }} size={24} />
            </div>
          </Card>
        </div>
      </Card.Section>
    </Card>
  );
}
